import React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import "./Articles.css";
import articlesData from "../../data/articles.json";
import SEO from "../../components/SEO";

// Canonical 33 service areas — only articles targeting these are shown in the listing.
// This prevents non-canonical sub-area, out-of-scope city, and corridor micro-pages
// from being crawled via the /articles/ listing page.
const CANONICAL_AREAS = new Set([
  "التجمع الأول", "التجمع الخامس", "التجمع الثالث",
  "مدينة نصر", "مصر الجديدة", "القطامية", "الحرفيين",
  "مدينة السلام", "جسر السويس",
  "المعادي", "زهراء المعادي", "صقر قريش",
  "حلوان", "المقطم", "المنيل",
  "الجيزة", "المهندسين", "الدقي", "العجوزة",
  "إمبابة", "الوراق", "المنيب",
  "الهرم", "فيصل", "6 أكتوبر", "حدائق الأهرام", "الشيخ زايد",
  "شبرا", "شبرا الخيمة", "المطرية",
  "الطريق الدائري", "الأوتوستراد", "صلاح سالم",
]);

const EDITORIAL_SLUGS = new Set([
  "اسعار-ونش-انقاذ-2026",
  "comprehensive-rescue-winch-services",
  "different-types-of-tow-trucks",
  "safety-procedures-car-breakdown",
  "towing-service-cost-factors",
  "modern-tow-truck-fleet-technologies",
  "complete-guide-rescue-towing-services",
  "towing-abbasiya-cairo",
  "towing-first-settlement-new-cairo",
  "towing-rehab-city-cairo",
  "prevent-car-breakdowns-maintenance-guide",
  "roadside-assistance-complete-guide",
  "towing-services-seasonal-weather-guide",
  "winchenqaz-reliable-towing-services",
]);

function normalizeArabic(text) {
  return String(text || "")
    .replace(/[أإآ]/g, "ا")
    .replace(/ة/g, "ه")
    .replace(/ى/g, "ي")
    .trim();
}

const CANONICAL_AREAS_NORM = new Set([...CANONICAL_AREAS].map(normalizeArabic));

function Articles() {
  const articles = articlesData.articles.filter((a) => {
    // Show editorial articles
    if (EDITORIAL_SLUGS.has(a.slug)) return true;
    // Show articles targeting one of the 33 canonical areas (guides + FAQs)
    if (a.targetArea && CANONICAL_AREAS_NORM.has(normalizeArabic(a.targetArea))) return true;
    return false;
  });

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('ar-EG', options);
  };

  return (
    <div className="articles-page">
      <SEO
        title="مقالات ونش الإنقاذ | نصائح وأدلة سحب السيارات"
        description="أحدث المقالات والنصائح حول خدمات ونش الإنقاذ وسحب السيارات: اختيار أفضل خدمة، أنواع الأوناش، إجراءات السلامة، الأسعار، ونصائح الصيانة لتفادي الأعطال."
        path="/articles"
      />
      <h1 className="articles-page-title">مقالات عن ونش الإنقاذ</h1>
      <p className="articles-page-description">
        أحدث المقالات والنصائح المتعلقة بخدمات ونش الإنقاذ وسحب السيارات ومساعدة السائقين على الطريق.
        تعرف على أفضل الممارسات والمعلومات المفيدة لكل سائق.
      </p>

      <div className="articles-grid">
        {articles.map((article) => (
          <div key={article.id} className="article-card">
            <div className="article-card-image">
              <img
                src={article.image || "/images/articles/default.jpg"}
                alt={article.title}
                loading="lazy"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/images/articles/default.jpg";
                }}
              />
            </div>
            <div className="article-card-content">
              <div className="article-card-date">{formatDate(article.date)}</div>
              <h2 className="article-card-title">{article.title}</h2>
              <div className="article-tags">
                {article.tags && article.tags.map((tag, idx) => (
                  <span key={idx} className="article-tag">{tag}</span>
                ))}
              </div>
              <p className="article-card-summary">{article.summary}</p>
              <Link to={`/articles/${article.slug}`} className="article-read-more">
                اقرأ المزيد <ChevronLeft size={18} />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Articles;
